/**
 * Retrieve all projects.
 * @param {string} token - The keycloak token used for authorization.
 * @returns {Promise<ProjectsInterface>} A promise that resolves to the list of projects.
 * @throws Will throw an error if the request fails.
 */

import { API_URL, apiRequest } from "../config/api";

export const getProjects = async (token: string): Promise<ProjectsInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/?size=1000&include_sites=true&t=${new Date().getTime()}`, {
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data:ProjectsInterface = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get a project by its ID with automatic token refresh.
 * @param {string} token - the keycloak token
 * @param {string} id - the ID of the project to get
 * @param {any} keycloak - optional keycloak instance for token refresh
 * @returns {Promise<ProjectInterface>} the project
 * @throws if there is an error
 */
export const getProjectById = async (token: string, id: string, keycloak?: any): Promise<ProjectInterface> => {
  try {
    const r = await apiRequest(`${API_URL}/projects/${id}?include_sites=true`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    }, keycloak);
    
    if (!r.ok) {
      if (r.status === 404) {
        throw new Error(`Project with ID ${id} not found`);
      }
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    const data:ProjectInterface = await r.json();
    return data;
  } catch(e: Error | any) {
    console.error('❌ getProjectById error:', e);
    throw new Error(e.message || 'Failed to load project');
  }
};

/**
 * Create a project.
 * @param {string} token - the keycloak token
 * @param {ProjectInterface} data - the data to create the project with
 * @returns {Promise<ProjectInterface>} the created project
 * @throws if there is an error
 */
export const createProject = async (token: string, data: ProjectInterface): Promise<ProjectInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/`,{
      method: 'POST',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    })
    const res:ProjectInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

  /**
   * Update a project by its ID.
   * @param {string} token - the keycloak token
   * @param {string} id - the ID of the project to update
   * @param {ProjectInterface} data - the data to update the project with
   * @returns {Promise<ProjectInterface>} the updated project
   * @throws if there is an error
   */
export const updateProject = async (token: string, id: string, data: ProjectInterface): Promise<any> => {
  try {
    const res = await fetch(`${API_URL}/projects/${id}`,{
      method: 'PUT',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    })

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

  /**
   * Delete a project by its ID.
   * @param {string} token - the keycloak token
   * @param {string} id - the ID of the project to delete
   * @returns {Promise<ProjectInterface>} the deleted project
   * @throws if there is an error
   */
export const deleteProject = async (token: string, id: string): Promise<ProjectInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}`,{
      method: 'DELETE',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const res:ProjectInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

  /**
   * Retrieve all phases of a project.
   * @param {string} token - The keycloak token used for authorization.
   * @param {string} id - The ID of the project to get the phases from.
   * @returns {Promise<ProjectPhaseInterface[]>} A promise that resolves to the list of phases.
   * @throws Will throw an error if the request fails.
   */
export const getProjectPhases = async (token: string, id: string): Promise<ProjectPhaseInterface[]> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/phases?size=1000`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data:ProjectPhaseInterface[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve a specific phase of a project by its ID and phase ID.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project.
 * @param {string} phaseId - The ID of the phase to retrieve.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the project phase.
 * @throws Will throw an error if the request fails.
 */

export const getProjectPhaseById = async (token: string, id: string, phaseId: string): Promise<ProjectPhaseInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/phases/${phaseId}`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data:ProjectPhaseInterface = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create a phase for a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to create the phase for.
 * @param {ProjectPhaseInterface} data - The data to create the phase with.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the created phase.
 * @throws Will throw an error if the request fails.
 */
export const createProjectPhase = async (token: string, id: string, data: ProjectPhaseInterface): Promise<any> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/phases`,{
      method: 'POST',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        ...data,
      })
    })
    const res:ProjectPhaseInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update a specific phase of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the phase for.
 * @param {string} phaseId - The ID of the phase to update.
 * @param {ProjectPhaseInterface} data - The data to update the phase with.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the updated phase.
 * @throws Will throw an error if the request fails.
 */
export const updateProjectPhase = async (token: string, id: string, phaseId: string, data: ProjectPhaseInterface): Promise<ProjectPhaseInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/phases/${phaseId}`,{
      method: 'PUT',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        phase_data: data,
      })
    })
    const res:ProjectPhaseInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Delete a specific phase of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to delete the phase from.
 * @param {string} phaseId - The ID of the phase to delete.
 * @returns {Promise<ProjectPhaseInterface>} A promise that resolves to the deleted phase.
 * @throws Will throw an error if the request fails.
 */
export const deleteProjectPhase = async (token: string, id: string, phaseId: string): Promise<ProjectPhaseInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/phases/${phaseId}`,{
      method: 'DELETE',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const res:ProjectPhaseInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve all users of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to retrieve the users of.
 * @returns {Promise<ProjectUsersResponse>} A promise that resolves to the list of users with roles.
 * @throws Will throw an error if the request fails.
 */
export const getProjectUsers = async (token: string, id: string): Promise<ProjectUsersResponse> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/users?size=1000`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data: ProjectUsersResponse = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get project user roles.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to get roles for.
 * @returns {Promise<any[]>} A promise that resolves to the list of available roles.
 * @throws Will throw an error if the request fails.
 */
export const getProjectUserRoles = async (token: string, id: string): Promise<any[]> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/users/roles`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data: any[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Add a user to a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to add the user to.
 * @param {string} userId - The ID of the user to add to the project.
 * @param {any} userData - The data to add the user with.
 * @returns {Promise<ProjectUserWithRole>} A promise that resolves to the added user.
 * @throws Will throw an error if the request fails.
 */
export const addUserToProject = async (token: string, id: string, _userId: string, userData: any): Promise<ProjectUserWithRole> => {
  try {
    // For creation, API requires: project_id, first_name, last_name, email
    const requestData: any = {
      project_id: id,
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email
    };

    // Add optional fields only if they exist
    if (userData.phone) {
      requestData.phone = userData.phone;
    }
    
    if (userData.project_role_id) {
      requestData.project_role_id = userData.project_role_id;
    }
    
    if (userData.escalation_level_id) {
      requestData.escalation_level_id = userData.escalation_level_id;
    }
    
    if (userData.department) {
      requestData.department = userData.department;
    }
    
    if (userData.status) {
      requestData.status = userData.status;
    }
    
    // Skills can be null or string
    if (userData.skills !== undefined) {
      requestData.skills = userData.skills || null;
    }

    const r = await fetch(`${API_URL}/projects/${id}/users`,{
      method: 'POST',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(requestData)
    });

    if (!r.ok) {
      const errorText = await r.text();
      console.error('addUserToProject: API error', r.status, errorText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}. Server response: ${errorText}`);
    }

    const res: ProjectUserWithRole = await r.json();
    console.log('addUserToProject: success', res);

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update a user in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the user in.
 * @param {string} userId - The ID of the user to update.
 * @param {any} userData - The data to update the user with.
 * @returns {Promise<ProjectUserWithRole>} A promise that resolves to the updated user.
 * @throws Will throw an error if the request fails.
 */
export const updateUserInProject = async (token: string, id: string, userId: string, userData: any): Promise<ProjectUserWithRole | null> => {
  try {
    // Build request data according to API specification
    // All fields are optional except id, project_id, user_id
    const requestData: any = {
      id: userData.id, // Required: record ID
      project_id: id,
      user_id: userId
    };

    // Add optional fields only if they exist
    if (userData.project_role_id) {
      requestData.project_role_id = userData.project_role_id;
    }
    
    if (userData.escalation_level_id) {
      requestData.escalation_level_id = userData.escalation_level_id;
    }
    
    if (userData.first_name) {
      requestData.first_name = userData.first_name;
    }
    
    if (userData.last_name) {
      requestData.last_name = userData.last_name;
    }
    
    if (userData.email) {
      requestData.email = userData.email;
    }
    
    if (userData.phone) {
      requestData.phone = userData.phone;
    }
    
    if (userData.department) {
      requestData.department = userData.department;
    }
    
    if (userData.status) {
      requestData.status = userData.status;
    }
    
    if (userData.skills) {
      requestData.skills = userData.skills;
    }

    const r = await fetch(`${API_URL}/projects/${id}/users/${userId}`,{
      method: 'PUT',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(requestData)
    });
    
    const text = await r.text();
    const res = text ? JSON.parse(text) : null;
    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Remove a user from a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to remove the user from.
 * @param {string} userId - The ID of the user to remove from the project.
 * @returns {Promise<ProjectUserWithRole>} A promise that resolves to the removed user.
 * @throws Will throw an error if the request fails.
 */
export const deleteUserFromProject = async (token: string, id: string, userId: string): Promise<ProjectUserWithRole> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/users/${userId}`,{
      method: 'DELETE',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const res: ProjectUserWithRole = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve all items of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to retrieve the items from.
 * @returns {Promise<ProjectItemInterface[]>} A promise that resolves to the list of items.
 * @throws Will throw an error if the request fails.
 */
export const getProjectItems = async (token: string, id: string): Promise<ProjectItemInterface[]> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/items`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data:ProjectItemInterface[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create a new item in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to create the item in.
 * @param {ProjectItemInterface} data - The data to create the item with.
 * @returns {Promise<ProjectItemInterface>} A promise that resolves to the created item.
 * @throws Will throw an error if the request fails.
 */
export const createProjectItem = async (token: string, id: string, data: ProjectItemInterface): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/projects/${id}/items`,{
      method: 'POST',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${response.statusText}. Server response: ${errorText}`);
    }

    return true;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update an item in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the item in.
 * @param {string} itemId - The ID of the item to update.
 * @param {ProjectItemInterface} data - The data to update the item with.
 * @returns {Promise<ProjectItemInterface>} A promise that resolves to the updated item.
 * @throws Will throw an error if the request fails.
 */
export const updateProjectItem = async (token: string, id: string, itemId: string, data: ProjectItemInterface): Promise<boolean> => {
  try {
    console.log('🔧 updateProjectItem called with:', {
      projectId: id,
      itemId: itemId,
      tokenLength: token?.length || 0,
      tokenPreview: token?.substring(0, 20) + '...'
    });

    const response = await fetch(`${API_URL}/projects/${id}/items/${itemId}`,{
      method: 'PUT',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    });

    console.log('📡 updateProjectItem response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ updateProjectItem error:', response.status, errorText);
      
      // Special handling for authentication errors
      if (response.status === 401) {
        console.error('🔐 Authentication error in updateProjectItem - token may be expired');
        const authError = new Error('Authentication failed. Please refresh the page and try again.');
        (authError as any).isAuthError = true;
        (authError as any).statusCode = 401;
        throw authError;
      }
      
      throw new Error(`HTTP ${response.status}: ${response.statusText}. Server response: ${errorText}`);
    }

    console.log('✅ updateProjectItem successful');
    return true;
  } catch(e: Error | any) {
    console.error('❌ updateProjectItem exception:', e);
    throw new Error(e.message || 'Failed to update project item');
  }
};

/**
 * Delete an item from a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to delete the item from.
 * @param {string} itemId - The ID of the item to delete.
 * @returns {Promise<ProjectItemInterface>} A promise that resolves to the deleted item.
 * @throws Will throw an error if the request fails.
 */
export const deleteProjectItem = async (token: string, id: string, itemId: string): Promise<ProjectItemInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/items/${itemId}`,{
      method: 'DELETE',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const res:ProjectItemInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Retrieve all sites of a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to retrieve the sites from.
 * @returns {Promise<ProjectSiteInterface[]>} A promise that resolves to the list of sites.
 * @throws Will throw an error if the request fails.
 */
export const getProjectSites = async (token: string, id: string): Promise<ProjectSiteInterface[]> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/sites?size=1000`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data:ProjectSiteInterface[] = await r.json();

    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create a new site in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to create the site in.
 * @param {ProjectSiteInterface} data - The data to create the site with.
 * @returns {Promise<ProjectSiteInterface>} A promise that resolves to the created site.
 * @throws Will throw an error if the request fails.
 */
export const createProjectSite = async (token: string, id: string, data: ProjectSiteItemInterface): Promise<ProjectSiteItemInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${id}/sites`,{
      method: 'POST',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(
        data
      )
    })
    const res:ProjectSiteItemInterface = await r.json();

    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update a site in a project.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} id - The ID of the project to update the site in.
 * @param {string} siteId - The ID of the site to update.
 * @param {ProjectSiteItemInterface} data - The data to update the site with.
 * @returns {Promise<ProjectSiteItemInterface>} A promise that resolves to the updated site.
 * @throws Will throw an error if the request fails.
 */
export const updateProjectSite = async (token: string, id: string, siteId: string, data: ProjectSiteItemInterface): Promise<ProjectSiteItemInterface> => {
  try {
    // Ensure project_id is included in the request body
    const requestData = {
      ...data,
      project_id: id
    };
    
    const r = await fetch(`${API_URL}/projects/${id}/sites/${siteId}`,{
      method: 'PUT',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(requestData)
    })
    
    if (!r.ok) {
      // Try to get response body for better error information
      let responseBody = '';
      try {
        responseBody = await r.text();
      } catch (e) {
        responseBody = 'Could not read response body';
      }
      
      throw new Error(`HTTP ${r.status}: ${r.statusText}. Response: ${responseBody}`);
    }
    
    // Check if response has content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
    const res:ProjectSiteItemInterface = await r.json();
      return res;
    }

    // For 204 No Content or other successful responses without JSON body
    // Return the input data as the "updated" site (optimistic update)
    return data;
  } catch(e: Error | any) {
    throw new Error(e.message || e);
  }
};

/**
 * Update a site directly by site ID.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} siteId - The ID of the site to update.
 * @param {any} data - The data to update the site with.
 * @returns {Promise<any>} A promise that resolves when the site is updated.
 * @throws Will throw an error if the request fails.
 */
export const updateSite = async (token: string, siteId: string, data: any): Promise<any> => {
  try {
    const r = await fetch(`${API_URL}/sites/${siteId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    });

    if (!r.ok) {
      // Try to get response body for better error information
      let responseBody = '';
      try {
        responseBody = await r.text();
      } catch (e) {
        responseBody = 'Could not read response body';
      }
      
      throw new Error(`HTTP ${r.status}: ${r.statusText}. Response: ${responseBody}`);
    }

    // Check if response has content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await r.json();
    }
    
    return true; // Success for 204 No Content
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to update site');
  }
};

// Keep the old place functions for backwards compatibility
/**
 * Get place information for a project item.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} projectId - The ID of the project.
 * @param {string} itemId - The ID of the item.
 * @returns {Promise<any>} A promise that resolves to the place information.
 * @throws Will throw an error if the request fails.
 */
export const getProjectItemPlace = async (token: string, projectId: string, itemId: string): Promise<any> => {
  try {
    const r = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/place`,{
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      }
    })
    const data = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create or update place information for a project item.
 * @param {string} token - The keycloak token used for authorization.
 * @param {string} projectId - The ID of the project.
 * @param {string} itemId - The ID of the item.
 * @param {any} data - The place data to save.
 * @returns {Promise<any>} A promise that resolves to the saved place information.
 * @throws Will throw an error if the request fails.
 */
export const saveProjectItemPlace = async (token: string, projectId: string, itemId: string, data: any): Promise<any> => {
  try {
    const r = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/place`,{
      method: 'POST',
      headers: {
        'content-type':'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    })
    const res = await r.json();
    return res;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Add asset to project item
 */
export const addAssetToProjectItem = async (
  token: string, 
  projectId: string, 
  itemId: string, 
  assetId: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/assets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ asset_id: assetId })
    });

    if (response.status === 409) {
      // Asset already linked to this item
      throw new Error('Asset is already linked to this item');
    }

    if (!response.ok) {
      throw new Error(`Failed to add asset to project item: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error adding asset to project item:', error);
    throw error;
  }
};

/**
 * Remove asset from project item
 */
export const removeAssetFromProjectItem = async (
  token: string, 
  projectId: string, 
  itemId: string, 
  assetId: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/assets/${assetId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to remove asset from project item: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error removing asset from project item:', error);
    throw error;
  }
};

/**
 * Get assets for project item
 */
export const getAssetsForProjectItem = async (
  token: string, 
  projectId: string, 
  itemId: string
): Promise<any[]> => {
  try {
    console.log('API: Fetching assets for item:', itemId);
    const response = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/assets`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('API: Response status:', response.status);
    
    if (!response.ok) {
      console.warn(`Assets API returned ${response.status} for item ${itemId}`);
      return [];
    }

    const data = await response.json();
    console.log('API: Raw response data:', data);
    
    const results = Array.isArray(data) ? data : data.results || [];
    console.log('API: Processed results:', results);
    
    return results;
  } catch (error) {
    console.error('Error getting assets for project item:', error);
    return [];
  }
};

/**
 * Get items for project asset
 */
export const getItemsForProjectAsset = async (
  token: string, 
  projectId: string, 
  assetId: string
): Promise<any[]> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/${assetId}/items`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get items for project asset: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error('Error getting items for project asset:', error);
    return [];
  }
};
